import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Card, Typography, Button, Alert, Spin } from "antd";
import { CircleCheck, CircleAlert, Home } from "lucide-react";
import Icon from "../../components/Icon";
import {
  createLeadFromToken,
  verifyLeadTicket,
} from "../../services/confirmTicket";
import { brandingConfig } from "../../theme/brandingConfig";

const { Title, Text } = Typography;

const darkCardBg = '#111827';
const textColor = '#ffffff';
const secondaryTextColor = '#bfbfbf';

const ConfirmTicket = () => {
  const location = useLocation();
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    async function verifyLeadTicketAndCreate() {
      const params = new URLSearchParams(location.search);

      const extractedData = {
        token: params.get("token"),
        fullName: params.get("fullName"),
        phoneNumber: params.get("phoneNumber"),
        subject: params.get("subject"),
        email: params.get("email"),
      };

      setStatus("loading");
      const verifyResult = await verifyLeadTicket(extractedData.token);

      if (!verifyResult.valid) {
        setStatus("failure");
        return;
      }

      const createLeadResult = await createLeadFromToken(
        extractedData.token,
        extractedData.fullName,
        extractedData.subject,
        extractedData.phoneNumber,
        extractedData.email
      );

      if (!createLeadResult.success) {
        setStatus("failure");
        return;
      }
      setStatus("success");
    }

    verifyLeadTicketAndCreate();
  }, []);

  const SuccessView = (
    <Card
      style={{
        padding: 32,
        textAlign: "center",
        backgroundColor: darkCardBg,
        borderRadius: 8,
      }}
      styles={{ body: { padding: 0 } }}
    >
      <Icon as={CircleCheck} size={80} strokeWidth={2.5} style={{ color: '#9FBDB1', marginBottom: 16 }} />
      <Title level={3} style={{ color: textColor }}>
        Ticket Confirmed Successfully!
      </Title>
      <Text style={{ color: textColor, display: 'block', marginBottom: 24 }}>
        Your ticket has been successfully verified and submitted.
      </Text>
      <Button
        type="primary"
        icon={<Icon as={Home} />}
        onClick={() => (window.location.href = "/")}
      >
        Go to Homepage
      </Button>
    </Card>
  );

  const FailureView = (
    <Card
      style={{
        padding: 32,
        textAlign: "center",
        backgroundColor: darkCardBg,
        borderRadius: 8,
      }}
      styles={{ body: { padding: 0 } }}
    >
      <Icon as={CircleAlert} size={80} strokeWidth={2.5} style={{ color: '#E68A4C', marginBottom: 16 }} />
      <Title level={3} style={{ color: textColor }}>
        Verification Failed
      </Title>
      <Alert
        type="error"
        message="Could not confirm your ticket."
        description="The link may be expired (tokens expire after 1 hour) or invalid. Please ensure you are using the most recent confirmation email."
        style={{ textAlign: "left", marginBottom: 24 }}
      />
      <Button
        onClick={() => (window.location.href = "/contact-support")}
      >
        Contact Support Again
      </Button>
    </Card>
  );

  const LoadingView = (
    <Card
      style={{
        padding: 32,
        textAlign: "center",
        backgroundColor: darkCardBg,
        borderRadius: 8,
      }}
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Spin size="large" style={{ marginBottom: 16 }} />
        <Title level={5} style={{ color: textColor }}>
          Verifying your ticket...
        </Title>
        <Text style={{ color: secondaryTextColor }}>
          Please wait, this may take a moment.
        </Text>
      </div>
    </Card>
  );

  return (
    <div
      style={{
        backgroundImage: `url(${brandingConfig.assets.emailBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: '32px 0',
        position: 'relative',
      }}
    >
      <div style={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        zIndex: 1,
      }} />
      <div style={{ maxWidth: 576, width: '100%', zIndex: 2, position: "relative", padding: '0 16px' }}>
        {status === "loading" && LoadingView}
        {status === "success" && SuccessView}
        {status === "failure" && FailureView}
      </div>
    </div>
  );
};

export default ConfirmTicket;
